import React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTran } from "@/hooks";

import { AppDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup } from "@/components/ui/radio-group";
import { RadioGroupItem } from "@/components/ui/radio-group";
import { MODULE_TABLE_HEADERS } from "../utils/roles-permissions";
import { cn } from "@/lib/utils/index";

const ACTIONS = ["create", "update", "view", "delete"];
const ROLE_NAME_ALLOWED_REGEX = /^[a-zA-Z0-9 _-]+$/;
const RESTRICTED_ROLE_NAMES = [
  "systemadmin",
  "businessunit",
  "superadmin",
  "solutionprovider",
  "bankadmin",
  "smeuser",
  "system_admin",
  "business_unit",
  "super_admin",
  "solution_provider",
  "reseller",
  "bank_admin",
  "sme_user",
];

const normalizeRoleName = (name = "") =>
  String(name).toLowerCase().trim().replace(/\s+/g, "_");

const isRestrictedRoleName = (name) =>
  RESTRICTED_ROLE_NAMES.includes(normalizeRoleName(name));

const normalizeActions = (actions = {}) => ({
  create: actions.create ?? true,
  update: actions.update ?? true,
  view: actions.view ?? true,
  delete: actions.delete ?? true,
});

const resolveModuleId = (item = {}, parentId = "") =>
  String(
    item.moduleName?.key ||
      item.key ||
      item.id ||
      item._id ||
      (parentId
        ? `${parentId}-${Math.random().toString(36).slice(2, 6)}`
        : `module-${Math.random().toString(36).slice(2, 6)}`),
  );

const normalizeModules = (modules = []) => {
  const mapItem = (item = {}, parentId = "") => {
    const baseId = resolveModuleId(item, parentId);
    const title =
      item.moduleName?.value ||
      item.value ||
      item.title ||
      item.name ||
      "Untitled module";

    const childrenFromSubModule = Array.isArray(item.subModule)
      ? item.subModule.map((sub) =>
          mapItem(
            {
              ...sub,
              moduleName: { key: sub.key, value: sub.value },
              subModule: sub.subModule,
            },
            baseId,
          ),
        )
      : [];

    const childrenFromChildren = Array.isArray(item.children)
      ? item.children.map((child) => mapItem(child, baseId))
      : [];

    const children =
      childrenFromSubModule.length > 0
        ? childrenFromSubModule
        : childrenFromChildren;

    return {
      id: String(baseId),
      title,
      actions: normalizeActions(item.actions || {}),
      children,
    };
  };

  return (modules || []).map((module) => mapItem(module));
};

// const PermissionRow = ({ item, onToggle }) => (
//   <div className="flex flex-wrap items-center gap-4 pl-4 text-sm text-muted-foreground">
//     {ACTIONS.map((actionKey) => (
//       <label
//         key={`${item.id}-${actionKey}`}
//         className="flex items-center gap-2"
//       >
//         <Checkbox
//           checked={Boolean(item.actions?.[actionKey])}
//           onCheckedChange={(checked) =>
//             onToggle(item.id, actionKey, Boolean(checked))
//           }
//         />
//         <span className="capitalize text-foreground">{actionKey}</span>
//       </label>
//     ))}
//   </div>
// );

const PermissionTableRow = ({ item, t, onToggle, isChild = false }) => (
  <tr className="border-b border-border">
    <td className="px-3 py-2">
      <div className={isChild ? "pl-6 text-muted-foreground" : "font-semibold"}>
        {t(item.title)}
      </div>
    </td>

    {ACTIONS.map((action) => (
      <td key={action} className="px-3 py-2 text-center">
        <Checkbox
          checked={Boolean(item.actions?.[action])}
          onCheckedChange={(checked) =>
            onToggle(item.id, action, Boolean(checked))
          }
        />
      </td>
    ))}
  </tr>
);

const mergeRolePermissions = (modules = [], rolePermissions = []) => {
  if (!Array.isArray(modules) || modules.length === 0) return [];
  if (!Array.isArray(rolePermissions) || rolePermissions.length === 0)
    return modules;

  const map = new Map();
  const collect = (items = [], parentId = "") => {
    items.forEach((item) => {
      if (!item || typeof item !== "object") return;
      const id = resolveModuleId(item, parentId);
      map.set(id, normalizeActions(item.actions || {}));
      const childItems = Array.isArray(item.children)
        ? item.children
        : Array.isArray(item.subModule)
          ? item.subModule
          : [];
      if (childItems.length > 0) {
        collect(childItems, id);
      }
    });
  };
  collect(rolePermissions);

  const apply = (items = []) =>
    items.map((item) => {
      const actions = map.has(item.id)
        ? { ...(item.actions || {}), ...map.get(item.id) }
        : item.actions;
      const children =
        Array.isArray(item.children) && item.children.length > 0
          ? apply(item.children)
          : [];
      return { ...item, actions, children };
    });

  return apply(modules);
};

const hasAnyPermissionSelected = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return false;
  return items.some((item) => {
    const actions = item?.actions || {};
    const hasAction = ACTIONS.some((actionKey) => Boolean(actions[actionKey]));
    if (hasAction) return true;
    return hasAnyPermissionSelected(item.children);
  });
};

const CreateRoleDialog = ({
  open,
  onOpenChange,
  onSave,
  modules = [],
  role = null,
  mode = "create",
  saving = false,
  existingRoles = [],
}) => {
  const { t } = useTran();
  const [roleName, setRoleName] = useState("");
  const [modulePermission, setModulePermission] = useState("all");
  const [errors, setErrors] = useState({});
  const [isCheckingRoleName, setIsCheckingRoleName] = useState(false);
  const roleCheckRef = useRef(0);

  const normalizedModules = useMemo(() => normalizeModules(modules), [modules]);
  const [permissionsState, setPermissionsState] = useState(normalizedModules);

  const currentRoleId = useMemo(
    () => role?._id || role?.id || role?.key || role?.role || null,
    [role],
  );
  const currentRoleName = useMemo(
    () => role?.name || role?.title || role?.key || role?.role || "",
    [role],
  );

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setIsCheckingRoleName(false);
    const initialName =
      role?.name || role?.title || role?.key || role?.role || role?.id || "";
    setRoleName(initialName);
    if (typeof role?.isPermitAll === "boolean") {
      setModulePermission(role.isPermitAll ? "all" : "custom");
    } else {
      setModulePermission("all");
    }
    if (
      role?.permissions &&
      Array.isArray(role.permissions) &&
      role.permissions.length > 0
    ) {
      setPermissionsState(
        mergeRolePermissions(normalizedModules, role.permissions),
      );
    } else {
      setPermissionsState(normalizedModules);
    }
  }, [open, normalizedModules, role]);

  useEffect(() => {
    if (!open) return;
    const trimmed = roleName.trim();
    if (!trimmed) {
      setIsCheckingRoleName(false);
      setErrors((prev) => (prev.roleName ? { ...prev, roleName: "" } : prev));
      return;
    }
    if (!ROLE_NAME_ALLOWED_REGEX.test(trimmed)) {
      setIsCheckingRoleName(false);
      return;
    }
    if (isRestrictedRoleName(trimmed)) {
      setIsCheckingRoleName(false);
      return;
    }

    const normalized = normalizeRoleName(trimmed);
    const normalizedCurrent = normalizeRoleName(currentRoleName);
    if (normalized && normalized === normalizedCurrent) {
      setIsCheckingRoleName(false);
      setErrors((prev) => (prev.roleName ? { ...prev, roleName: "" } : prev));
      return;
    }

    const requestId = ++roleCheckRef.current;
    setIsCheckingRoleName(true);
    const timer = setTimeout(() => {
      const hasDuplicate = (existingRoles || []).some((item) => {
        const itemName = normalizeRoleName(
          item?.name || item?.title || item?.key || item?.role || "",
        );
        if (!itemName || itemName !== normalized) return false;
        const itemId = item?._id || item?.id || item?.key || item?.role || null;
        if (
          currentRoleId &&
          itemId &&
          String(itemId) === String(currentRoleId)
        ) {
          return false;
        }
        return true;
      });

      if (roleCheckRef.current !== requestId) return;

      setErrors((prev) => ({
        ...prev,
        roleName: hasDuplicate ? t("Role name already exists.") : "",
      }));
      setIsCheckingRoleName(false);
    }, 400);

    return () => clearTimeout(timer);
  }, [currentRoleId, currentRoleName, existingRoles, open, roleName, t]);

  const updatePermission = useCallback((targetId, action, checked) => {
    const updateItems = (items) =>
      items.map((item) => {
        if (item.id === targetId) {
          return {
            ...item,
            actions: { ...(item.actions || {}), [action]: checked },
          };
        }
        if (item.children) {
          return { ...item, children: updateItems(item.children) };
        }
        return item;
      });
    setPermissionsState((prev) => updateItems(prev));
    setErrors((prev) =>
      prev.permissionsState ? { ...prev, permissionsState: "" } : prev,
    );
  }, []);

  const handleSave = () => {
    const trimmedRoleName = roleName.trim();
    const nextErrors = {};
    if (!trimmedRoleName) {
      nextErrors.roleName = t("Role name is required");
    } else if (!ROLE_NAME_ALLOWED_REGEX.test(trimmedRoleName)) {
      nextErrors.roleName = t(
        "Role name can only contain letters, numbers, spaces, underscores, and hyphens.",
      );
    } else if (isRestrictedRoleName(trimmedRoleName)) {
      nextErrors.roleName = t(
        "This role name is restricted. Please choose another.",
      );
    }

    if (
      modulePermission === "custom" &&
      !hasAnyPermissionSelected(permissionsState)
    ) {
      nextErrors.permissionsState = t("Select at least one permission.");
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const transformPayload = (items) =>
      items.map(({ id, title, actions, children }) => ({
        key: id,
        title,
        actions,
        children:
          children && children.length > 0 ? transformPayload(children) : [],
      }));

    const payload = {
      id: role?._id || role?.id || role?.key || role?.role || undefined,
      name: trimmedRoleName,
      permissions: transformPayload(permissionsState),
      isPermitAll: modulePermission === "all",
    };
    onSave?.(payload);
  };

  const title = mode === "edit" ? t("Edit role") : t("Create a new role");
  const actionLabel = mode === "edit" ? t("Update Role") : t("Save Role");
  const trimmedRoleName = roleName.trim();
  const hasValidRoleNameFormat = trimmedRoleName
    ? ROLE_NAME_ALLOWED_REGEX.test(trimmedRoleName)
    : false;
  const isSaveDisabled =
    saving ||
    isCheckingRoleName ||
    !trimmedRoleName ||
    !hasValidRoleNameFormat ||
    isRestrictedRoleName(roleName) ||
    errors.roleName ||
    (modulePermission === "custom" &&
      !hasAnyPermissionSelected(permissionsState));

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={t("Create or update a role and define access permissions.")}
      size="lg"
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={saving}
          >
            {t("Cancel")}
          </Button>
          <Button onClick={handleSave} disabled={isSaveDisabled}>
            {saving ? t("Saving...") : actionLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <Label>
          {t("Role Name")} <span className="text-destructive ml-1">*</span>
        </Label>
        <Input
          value={roleName}
          onChange={(event) => {
            const value = event.target.value;
            setRoleName(value);
            const trimmed = value.trim();
            let nextError = "";
            if (trimmed && !ROLE_NAME_ALLOWED_REGEX.test(trimmed)) {
              nextError = t(
                "Role name can only contain letters, numbers, spaces, underscores, and hyphens.",
              );
            } else if (trimmed && isRestrictedRoleName(trimmed)) {
              nextError = t(
                "This role name is restricted. Please choose another.",
              );
            }
            setErrors((prev) => ({ ...prev, roleName: nextError }));
          }}
          placeholder={t("Enter a role name")}
        />
        {errors.roleName ? (
          <p className="text-xs text-destructive">{errors.roleName}</p>
        ) : null}
        {isCheckingRoleName ? (
          <p className="text-xs text-muted-foreground">
            {t("Checking role name availability...")}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label>
          {t("Module Permission")}{" "}
          <span className="text-destructive ml-1">*</span>
        </Label>
        <RadioGroup
          value={modulePermission}
          onValueChange={(value) => {
            setModulePermission(value);

            if (errors.modulePermission || errors.permissionsState) {
              setErrors((prev) => ({
                ...prev,
                modulePermission: "",
                permissionsState: "",
              }));
            }
          }}
          className="flex flex-wrap items-center gap-6 text-sm text-foreground"
        >
          {[
            { value: "all", label: t("Access to All") },
            { value: "custom", label: t("Access to Custom") },
          ].map((option) => (
            <div key={option.value} className="flex items-center gap-2">
              <RadioGroupItem
                value={option.value}
                id={`module-permission-${option.value}`}
              />
              <Label
                htmlFor={`module-permission-${option.value}`}
                className="cursor-pointer"
              >
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
        {errors.modulePermission ? (
          <p className="text-xs text-destructive">
            {t(errors.modulePermission)}
          </p>
        ) : null}
      </div>

      {modulePermission === "custom" && (
        <div className="flex-1 flex flex-col gap-y-2 min-h-0">
          <Label>
            {t("Permission List")}{" "}
            <span className="text-destructive ml-1">*</span>
          </Label>
          <div className="rounded-radius border border-border bg-muted/20 flex-1 overflow-y-auto">
            <div className="">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 bg-background">
                  <tr className="border-b border-border text-primary">
                    {MODULE_TABLE_HEADERS.map((header) => (
                      <th
                        key={header}
                        className={cn(
                          "px-3 py-2 font-medium",
                          header === "Module" ? "text-left" : "text-center",
                        )}
                      >
                        {t(header)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {permissionsState.map((item) => {
                    const hasChildren =
                      Array.isArray(item.children) && item.children.length > 0;

                    return (
                      <React.Fragment key={item.id}>
                        {hasChildren && (
                          <tr className="bg-muted/30 font-semibold">
                            <td
                              colSpan={5}
                              className="px-3 py-2 text-foreground"
                            >
                              {t(item.title)}
                            </td>
                          </tr>
                        )}

                        {!hasChildren && (
                          <PermissionTableRow
                            item={item}
                            t={t}
                            onToggle={updatePermission}
                          />
                        )}

                        {hasChildren &&
                          item.children.map((child) => (
                            <PermissionTableRow
                              key={child.id}
                              item={child}
                              t={t}
                              onToggle={updatePermission}
                              isChild
                            />
                          ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {errors.permissionsState ? (
            <p className="text-xs text-destructive">
              {t(errors.permissionsState)}
            </p>
          ) : null}
        </div>
      )}
    </AppDialog>
  );
};

export default CreateRoleDialog;
